import { FC, KeyboardEvent, TouchEvent, TouchList, useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@purpur/library/icon';
import { chevronLeft } from '@purpur/library/icon/assets/chevron-left';
import { close } from '@purpur/library/icon/assets/close';
import Slider, { Settings as SliderSettings } from 'react-slick';

import { useInitializeTranslations } from '@telialt/common-cms/src/useTranslations';
import { Modal } from '@telialt/common-modal/src';
import { isEmpty } from '@telialt/utils/src/helpers';

import {
    BREAKPOINT_LARGE,
    BREAKPOINT_MEDIUM,
    BREAKPOINT_SMALL,
    BREAKPOINT_XLARGE,
    THUMBNAILS_PER_ROW_LIGHT_BOX,
} from './constants';
import {
    BackToTheProduct,
    GalleryLightBoxThumbnail,
    GalleryLightBoxThumbnailImg,
    GalleryLightBoxThumbnailsInRows,
    ImageBoundary,
    ImageWrapper,
    LightBoxAccessibilityDiv,
    ModalCloseButton,
    SliderFullScreen,
    SliderWrapper,
} from './GalleryLightBox.styles';
import { useArrowKeys, useWindowWidth } from './hooks';
import { NavArrowButton } from './NavArrowButton';
import { PaginationDots } from './PaginationDots';
import { GALLERY_TEST_ID } from './test-ids';
import type { GalleryImage } from './types';

export type GalleryLightBoxProps = {
    images: GalleryImage[];
    thumbs?: GalleryImage[];
    selectedId: number;
    onSwipe?: (id: number) => void;
    onClose?: () => void;
    onThumbnailSelect?: (id: number) => void;
    scrollThumbs?: boolean;
    isModalOpen: boolean;
};

type Offset = { x: number; y: number };

export const GalleryLightBox: FC<GalleryLightBoxProps> = ({
    images,
    selectedId: selectedIdProp,
    onSwipe,
    onClose,
    thumbs,
    onThumbnailSelect,
    scrollThumbs = false,
    isModalOpen,
}) => {
    const { t } = useInitializeTranslations();

    const sliderMain = useRef<Slider>(null);
    const sliderThumbs = useRef<Slider>(null);
    const windowWidth = useWindowWidth();

    const [isZooming, setIsZooming] = useState(false);
    const [isPinching, setIsPinching] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [pinchDistance, setPinchDistance] = useState(0);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

    const [selectedId, setSelectedId] = useState<number>(selectedIdProp);
    const [isThumbnailFocused, setIsThumbnailFocused] = useState(false);

    const thumbsToScroll =
        (windowWidth >= BREAKPOINT_SMALL && windowWidth < BREAKPOINT_MEDIUM && THUMBNAILS_PER_ROW_LIGHT_BOX.MOBILE) ||
        (windowWidth >= BREAKPOINT_MEDIUM && windowWidth < BREAKPOINT_LARGE && THUMBNAILS_PER_ROW_LIGHT_BOX.TABLET) ||
        (windowWidth >= BREAKPOINT_LARGE && windowWidth < BREAKPOINT_XLARGE && THUMBNAILS_PER_ROW_LIGHT_BOX.DESKTOP) ||
        THUMBNAILS_PER_ROW_LIGHT_BOX.LDESKTOP;

    const thumbnails = thumbs || images;

    const prevImage = () => sliderMain?.current?.slickPrev();
    const nextImage = () => sliderMain?.current?.slickNext();

    const onArrowRight = () => {
        sliderMain?.current?.slickNext();
        document
            .getElementById(thumbnails[thumbnails.length - 1 === selectedId ? 0 : selectedId + 1].src + 'lightBox')
            ?.focus();
    };

    const onArrowLeft = () => {
        sliderMain?.current?.slickPrev();
        document
            .getElementById(thumbnails[selectedId === 0 ? thumbnails.length - 1 : selectedId - 1].src + 'lightBox')
            ?.focus();
    };

    useArrowKeys(onArrowRight, onArrowLeft, isThumbnailFocused);

    // Slider only works when it's focused. Ref : https://github.com/akiran/react-slick/issues/1121#issuecomment-374849363;
    useEffect(() => {
        sliderMain?.current?.innerSlider?.list?.setAttribute('tabindex', '0');
        sliderMain?.current?.innerSlider?.list?.focus();
    }, []);

    const sliderGetRowCount = (thumbsToShow: number) => {
        const count = images.length;
        const rows = thumbsToShow > 0 && Math.ceil(count / thumbsToShow + 1);

        return rows || undefined;
    };

    const handleTabEnterPress = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
        if ((e.key === 'Enter' || e.key === ' ') && index !== selectedId) {
            e.preventDefault();
            setSelectedId(index);
            goToImage(index);
            onThumbnailSelect?.(index);
        }
    };

    const handleThumbnailClick = (index: number) => {
        setPanOffset({ x: 0, y: 0 });
        setZoomLevel(1);
        setSelectedId(index);
        goToImage(index);
        onThumbnailSelect?.(index);
    };

    const isSafariBrowser = navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1;

    // Without this Safari browser on iPhone thumbnail click doesn't work
    const handleThumbnailClickForIphone = (index: number) => {
        if (isSafariBrowser && 'ontouchstart' in window) {
            setPanOffset({ x: 0, y: 0 });
            setZoomLevel(1);
            setSelectedId(index);
            goToImage(index);
            onThumbnailSelect?.(index);
        }
    };

    // Without this Safari browser pc thumbnail click doesn't work
    const handleThumbnailClickForSafari = (index: number) => {
        if (!(isSafariBrowser && 'ontouchstart' in window)) {
            setPanOffset({ x: 0, y: 0 });
            setZoomLevel(1);
            setSelectedId(index);
            goToImage(index);
            onThumbnailSelect?.(index);
        }
    };

    const goToThumb = useCallback(
        (index: number) => {
            if (scrollThumbs) {
                const thumbsPage = Math.floor(index / thumbsToScroll);
                const thumbsSlide = thumbsPage * thumbsToScroll;

                sliderThumbs.current?.slickGoTo(thumbsSlide);
            } else {
                sliderThumbs.current?.slickGoTo(index);
            }
        },
        [scrollThumbs, thumbsToScroll],
    );

    const goToImage = (index: number) => {
        return sliderMain.current?.slickGoTo(index);
    };

    useEffect(() => {
        if (selectedId !== selectedIdProp) {
            setSelectedId(selectedIdProp);
            goToThumb(selectedIdProp);
            goToImage(selectedIdProp);
        }
    }, [goToThumb, selectedId, selectedIdProp]);

    if (isEmpty(images) || !t) {
        return null;
    }

    const calculatePinchDistance = (touches: TouchList) => {
        const touch1 = touches.item(0);
        const touch2 = touches.item(1);

        return Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
    };

    const handleTouchStart = (event: TouchEvent<HTMLImageElement>) => {
        const touches = event.touches;

        if (touches.length === 1 && zoomLevel === 1) {
            setIsZooming(false);
        }

        if (touches.length === 1) {
            // If only one finger is on the screen, initiate panning
            setPanStart({ x: touches[0].clientX, y: touches[0].clientY });
        }

        if (touches.length === 2) {
            setIsZooming(true);
            // If two fingers are on the screen, store the initial pinch distance
            setPinchDistance(calculatePinchDistance(touches));
            const midPoint = {
                x: (touches[0].clientX + touches[1].clientX) / 2 - panOffset.x,
                y: (touches[0].clientY + touches[1].clientY) / 2 - panOffset.y,
            };
            setPanStart(midPoint);
        }
    };

    const handleTouchEnd = (event: TouchEvent<HTMLImageElement>) => {
        const touches = event.touches;

        if (zoomLevel === 1) {
            setPanOffset({ x: 0, y: 0 });
            setIsZooming(false);
        }
        if (touches.length === 0) {
            setIsPinching(false);
        }
    };

    const handleTouchMove = (event: TouchEvent<HTMLImageElement>) => {
        const touches = event.touches;
        const sensitivity = 1;

        if (touches.length === 1 && zoomLevel > 1 && !isPinching) {
            // Single-finger pan when zoomed in
            const newPanStart = {
                x: touches[0].clientX,
                y: touches[0].clientY,
            };

            const newPanOffset = {
                x: newPanStart.x - panStart.x + panOffset.x,
                y: newPanStart.y - panStart.y + panOffset.y,
            };

            // Limit pan offset to stay within image boundaries
            const limitedPanOffset = limitPanOffset(newPanOffset);
            setPanOffset(limitedPanOffset);
            setPanStart(newPanStart);
        }

        if (touches.length === 2) {
            setIsZooming(true);
            setIsPinching(true);

            // If two fingers are on the screen, calculate pinch distance
            const currentPinchDistance = calculatePinchDistance(touches);

            // Calculate the pinch distance change
            const distanceChange = currentPinchDistance - pinchDistance;

            // Use a scaling factor to adjust the zoom level based on the pinch distance change
            const newZoomLevel = Math.max(1, Math.min(2.5, zoomLevel + distanceChange / 100));

            // Calculate the midpoint between the two fingers
            const midPoint = {
                x: (touches[0].clientX + touches[1].clientX) / 2,
                y: (touches[0].clientY + touches[1].clientY) / 2,
            };

            const deltaX = (midPoint.x - panStart.x) * sensitivity;
            const deltaY = (midPoint.y - panStart.y) * sensitivity;

            // Calculate the pan offset based on the pinch movement
            const newPanOffset = {
                x: panOffset.x + deltaX,
                y: panOffset.y + deltaY,
            };

            // Limit pan offset to stay within image boundaries
            const limitedPanOffset = limitPanOffset(newPanOffset);

            // Check if newZoomLevel is a valid number, otherwise set it to 1
            if (!isNaN(newZoomLevel)) {
                setPanOffset(limitedPanOffset);
                setPanStart(midPoint);
                setZoomLevel(newZoomLevel);
                setPinchDistance(currentPinchDistance); // Update the pinch distance for the next pinch event
            } else {
                setZoomLevel(1);
            }
        }
    };

    const limitPanOffset = (offset: Offset) => {
        const imageWidth = document.getElementById(images[selectedId].src)?.clientWidth;
        const imageHeight = document.getElementById(images[selectedId].src)?.clientHeight;

        // Calculate the maximum allowed pan values
        const maxPanX = ((imageWidth ?? 0) * (zoomLevel - 1)) / (2 * zoomLevel);
        const maxPanY = ((imageHeight ?? 0) * (zoomLevel - 1)) / (2 * zoomLevel);

        // Limit pan offset to stay within image boundaries
        const limitedPanOffset = {
            x: Math.max(-maxPanX, Math.min(maxPanX, offset.x)),
            y: Math.max(-maxPanY, Math.min(maxPanY, offset.y)),
        };

        return limitedPanOffset;
    };

    const sliderSettings: SliderSettings = {
        arrows: false,
        dots: false,
        fade: false,
        initialSlide: selectedIdProp,
        infinite: images.length > 1,
        slidesToShow: 1,
        slidesToScroll: 1,
        lazyLoad: 'ondemand',
        centerMode: false,
        dotsClass: '',
        afterChange: onSwipe,
        appendDots: PaginationDots,
        swipe: !isZooming,
        touchMove: !isZooming,
    };

    const settingsThumbs: SliderSettings = {
        rows: sliderGetRowCount(thumbsToScroll),
        arrows: false,
        dots: false,
        infinite: false,
        slidesPerRow: thumbsToScroll,
        lazyLoad: 'ondemand',
        swipeToSlide: true,
        focusOnSelect: false,
    };

    const thumbnailsCarousel = (
        <GalleryLightBoxThumbnailsInRows>
            <Slider ref={sliderThumbs} {...settingsThumbs}>
                {thumbnails?.map((item, i) => (
                    <LightBoxAccessibilityDiv key={item.src}>
                        <GalleryLightBoxThumbnail
                            id={item.src + 'lightBox'}
                            tabIndex={0}
                            $isActive={i === selectedId}
                            className={(i === selectedId && 'active') || ''}
                            onKeyDown={(e) => handleTabEnterPress(i, e)}
                            onClick={() => handleThumbnailClick(i)}
                            onMouseDown={() => handleThumbnailClickForSafari(i)}
                            onPointerDown={() => handleThumbnailClickForSafari(i)}
                            onPointerEnter={() => handleThumbnailClickForIphone(i)}
                            onFocus={() => setIsThumbnailFocused(true)}
                            onBlur={() => setIsThumbnailFocused(false)}
                            role="button"
                        >
                            <GalleryLightBoxThumbnailImg src={item.src} alt={item.altText} />
                        </GalleryLightBoxThumbnail>
                    </LightBoxAccessibilityDiv>
                ))}
            </Slider>
        </GalleryLightBoxThumbnailsInRows>
    );

    return (
        <Modal
            dataTest={GALLERY_TEST_ID.LIGHTBOX}
            onHide={onClose}
            isScrollable={false}
            isShown={isModalOpen}
            type="fullscreen"
            header={
                <>
                    <BackToTheProduct variant="text" onClick={() => onClose?.()}>
                        <Icon svg={chevronLeft} size="md" ally-title="chevron left icon" />
                        {t['eshop.product_page.gallery.zoom.go_back']}
                    </BackToTheProduct>
                    <ModalCloseButton data-test={GALLERY_TEST_ID.LIGHTBOX_CLOSE_BTN} onClick={onClose}>
                        <Icon svg={close} size="lg" ally-title="close icon" />
                    </ModalCloseButton>
                </>
            }
        >
            <SliderWrapper>
                <SliderFullScreen ref={sliderMain} {...sliderSettings}>
                    {images.map((item, i) => (
                        <ImageBoundary className="slide" key={item.src}>
                            <ImageWrapper
                                data-test={`${GALLERY_TEST_ID.LIGHTBOX_IMAGE}-${i}`}
                                id={item.src}
                                src={item.src}
                                alt={item.altText}
                                $scale={zoomLevel}
                                $panOffset={panOffset}
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                                onTouchMove={handleTouchMove}
                            />
                        </ImageBoundary>
                    ))}
                </SliderFullScreen>
                <NavArrowButton
                    direction="backward"
                    onClick={prevImage}
                    dataTest={GALLERY_TEST_ID.LIGHTBOX_PREV_BTN}
                    shown="desktop-only"
                />
                <NavArrowButton
                    direction="forward"
                    onClick={nextImage}
                    dataTest={GALLERY_TEST_ID.LIGHTBOX_NEXT_BTN}
                    shown="desktop-only"
                />
            </SliderWrapper>
            {thumbnails.length > 1 ? thumbnailsCarousel : null}
        </Modal>
    );
};
